import { CARE_ORDER_BLOTTER_PREFIX } from '@src/containers/Blotters/CareOrders/types';
import type { CareOrdersPanelConfig } from '@src/types/LayoutConfig';
import type { TabProps } from '@talos/kyoko';
import { isTradingBlotterPanel } from './blotterTabsMigrationHelpers';
import type { AppConfigStateWithLegacyLayoutConfigs } from './legacyLayoutConfigs.types';

const MY_TAB_ID = 'my-care-orders';
export const CARE_ORDERS_PANEL_MIGRATION_VERSION = 1;

function shouldRunCareOrdersPanelMigration(config: { careOrdersPanelMigrationVersion?: number }): boolean {
  const v = config.careOrdersPanelMigrationVersion;
  return v == null || v < CARE_ORDERS_PANEL_MIGRATION_VERSION;
}

type CareOrdersTabProps = TabProps & { showAllDecimals?: boolean; expandAllGroups?: boolean };
type LegacyCareOrdersPanelParams = Omit<CareOrdersPanelConfig, 'tabs'> & {
  tabs?: CareOrdersPanelConfig['tabs'];
  showAllDecimals?: boolean;
  expandAllGroups?: boolean;
};

function createMyTab(params: { showAllDecimals?: boolean; expandAllGroups?: boolean }): CareOrdersTabProps {
  return {
    id: MY_TAB_ID,
    label: 'My Tab',
    closable: true,
    editable: true,
    showAllDecimals: params.showAllDecimals ?? false,
    expandAllGroups: params.expandAllGroups ?? true,
  };
}

/**
 * Converts legacy care orders panel params into tabbed params
 * - seeds `My Tab` from legacy panel fields when tabs are absent
 *   (`All` is injected at runtime by usePersistedTabs via excludedIDs/defaultInitialItems)
 * - copies (non-destructively) legacy panel blotter state key from `${prefix}/${panelId}`
 *   to `${prefix}/${panelId}/my-care-orders`
 */
export function migrateCareOrdersPanelToTabs(initialConfig: unknown) {
  const config = initialConfig as AppConfigStateWithLegacyLayoutConfigs & { careOrdersPanelMigrationVersion?: number };
  if (!shouldRunCareOrdersPanelMigration(config)) {
    return;
  }

  for (const layoutConfig of config.spacesConfigs?.trading?.layouts ?? []) {
    const panels = layoutConfig?.dockViewLayout?.panels;
    if (panels == null) {
      continue;
    }

    for (const [panelId, panel] of Object.entries(panels)) {
      if (panel == null || !isTradingBlotterPanel(panel, 'careOrders')) {
        continue;
      }

      const panelParams = (panel.params ?? {}) as LegacyCareOrdersPanelParams;
      const existingItems = panelParams.tabs?.items;
      const hasExistingTabs = Array.isArray(existingItems) && existingItems.length > 0;
      if (hasExistingTabs) {
        continue;
      }
      const showAllDecimals = panelParams.showAllDecimals;
      const expandAllGroups = panelParams.expandAllGroups;

      const items = [createMyTab({ showAllDecimals, expandAllGroups })];

      const nextBlotterTablesState = { ...(panelParams.blotterTablesState ?? {}) };
      const oldBlotterID = `${CARE_ORDER_BLOTTER_PREFIX}/${panelId}`;
      const myTabBlotterID = `${CARE_ORDER_BLOTTER_PREFIX}/${panelId}/${MY_TAB_ID}`;
      if (nextBlotterTablesState[oldBlotterID] != null && nextBlotterTablesState[myTabBlotterID] == null) {
        nextBlotterTablesState[myTabBlotterID] = nextBlotterTablesState[oldBlotterID];
      }

      const nextParams: CareOrdersPanelConfig = {
        ...panelParams,
        type: 'careOrders',
        tabs: {
          selectedIndex: 0,
          items,
        },
        blotterTablesState: nextBlotterTablesState,
      };
      panel.params = nextParams;
    }
  }

  config.careOrdersPanelMigrationVersion = CARE_ORDERS_PANEL_MIGRATION_VERSION;
}
